import { type MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance, AxiosResponse } from 'axios';
import type { InvoiceFormFieldValues } from 'modules/Invoices/InvoiceForm/InvoiceForm.types';
import { useSelector } from 'react-redux';
import { invoiceFormToData } from 'shared/serializer';
import type { StoreState } from 'types/storeTypes';
import { type InvoiceResponse, InvoiceUpdateParamsSchema } from './invoice-schemas';
import { type ApiError, addActionNameToMutationResult, useLegacyAxiosInstance } from './utils';

type UpdateInvoiceVariables = {
  id: number;
  data: InvoiceFormFieldValues;
};

export async function updateInvoiceFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateInvoiceVariables,
  constants: StoreState['app']['constants'],
) {
  const instance = await axiosInstance;
  const serialized = invoiceFormToData(variables.data, constants);
  const validated = InvoiceUpdateParamsSchema.parse(serialized);
  return instance.put<InvoiceResponse>(`/invoices/${variables.id}`, validated);
}

export function useUpdateInvoice(
  options: MutationOptions<AxiosResponse<InvoiceResponse>, ApiError, UpdateInvoiceVariables> = {},
) {
  const axiosInstance = useLegacyAxiosInstance();
  const constants = useSelector((state: StoreState) => state.app.constants);
  const queryClient = useQueryClient();

  const mutation = useMutation<AxiosResponse<InvoiceResponse>, ApiError, UpdateInvoiceVariables>({
    mutationFn: variables => updateInvoiceFn(axiosInstance, variables, constants),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.id] });
    },
    ...options,
  });

  return addActionNameToMutationResult<
    AxiosResponse<InvoiceResponse>,
    ApiError,
    UpdateInvoiceVariables,
    'updateInvoice'
  >('updateInvoice', mutation);
}
